import { Head, router, useForm } from "@inertiajs/react"

import AccountForm, {
  type BusinessProfileForm,
  DEFAULT_HOURS,
} from "@/components/accounts/account-form"
import type { PlacesPickerSelection } from "@/components/places-picker"
import RecordForm from "@/components/record-form"
import RecordPageHeader from "@/components/ui/record-page-header"
import AdminLayout from "@/layouts/admin-layout"
import {
  adminAccountPath,
  adminAccountsPath,
  businessDetailsAdminAccountsPath,
  businessSearchAdminAccountsPath,
  editAdminAccountPath,
} from "@/routes"
import type { Account, BreadcrumbItem } from "@/types"

interface Partner {
  uid: string
  name: string
}

interface BusinessCategoryOption {
  value: string
  label: string
}

export default function EditAccount({
  account,
  partners,
  business_categories,
}: {
  account: Account & {
    partner_uid?: string
    landing_page_generated?: boolean
    landing_page_heading_override?: string | null
    landing_page_subheading_override?: string | null
    website_selection?: PlacesPickerSelection | null
    business_profile?: BusinessProfileForm | null
  }
  partners: Partner[]
  business_categories: BusinessCategoryOption[]
}) {
  const { data, setData, patch, processing, errors } = useForm({
    account: {
      name: account.name,
      logo: null as File | null,
      state: account.state,
      partner_uid: account.partner_uid ?? "",
      demo_mode: account.demo_mode ?? false,
      landing_page_cta_enabled: account.landing_page_cta_enabled ?? true,
      landing_page_heading_override:
        account.landing_page_heading_override ?? "",
      landing_page_subheading_override:
        account.landing_page_subheading_override ?? "",
      website_selection: account.website_selection ?? null,
      business_profile: {
        business_name: account.business_profile?.business_name ?? "",
        phone: account.business_profile?.phone ?? "",
        address: account.business_profile?.address ?? "",
        website: account.business_profile?.website ?? "",
        description: account.business_profile?.description ?? "",
        services: account.business_profile?.services ?? "",
        business_category: account.business_profile?.business_category ?? "",
        hours:
          account.business_profile?.hours &&
          account.business_profile.hours.length > 0
            ? account.business_profile.hours
            : DEFAULT_HOURS,
        hours_status: account.business_profile?.hours_status ?? "open_no_hours",
      } as BusinessProfileForm,
    },
  })

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Accounts",
      href: adminAccountsPath(),
    },
    {
      title: account.name,
      href: adminAccountPath({ id: account.uid }),
    },
    {
      title: "Edit",
      href: editAdminAccountPath({ id: account.uid }),
    },
  ]

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(adminAccountPath({ id: account.uid }), {
      forceFormData: true,
    })
  }

  function handleCancel() {
    router.visit(adminAccountPath({ id: account.uid }))
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${account.name}`} />
      <div className="flex h-full w-full flex-col gap-6 p-4">
        <div className="mx-auto w-full max-w-2xl">
          <RecordPageHeader title={account.name} state={account.state} />
        </div>
        <RecordForm
          submitLabel="Save"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          processing={processing}
        >
          <AccountForm
            data={data}
            setData={setData}
            errors={errors}
            logoUrl={account.logo_url ?? null}
            partners={partners}
            businessCategories={business_categories}
            landingPageGenerated={account.landing_page_generated ?? false}
            searchPath={businessSearchAdminAccountsPath()}
            detailsPath={businessDetailsAdminAccountsPath()}
          />
        </RecordForm>
      </div>
    </AdminLayout>
  )
}
